import React, { useEffect, useState } from 'react';
import '../App.css';
import { Button, Modal, message, Empty, Card, Row, Col, Space, Spin, Tag, Input } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PageLayout from '../components/PageLayout';
import Heading from '../components/Heading.js';
import Divider from '../components/Divider.js';
import ImageUpload from '../components/ImageUpload.js';
import { useNavigate, useParams } from 'react-router-dom';
import { getPropertyConfig, getPropertyList, updatePropertyImages } from '../apiCalls.js';
import { getStoredUser } from '../utils/safeLocalStorage.js';
import { isSuperAdmin } from '../utils/roleUtils';
import { IMAGE_SLOT_DESCRIPTIONS, sortImageSlots, withManagedImageSlots } from '../utils/imageSlots.js';
import * as Sentry from "@sentry/react";

const ImageManager = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { propertyID } = useParams();
    const [companyName, setCompanyName] = useState('');
    const [property, setProperty] = useState('');
    const [propList, setPropList] = useState([]);
    const [activePropertyName, setActivePropertyName] = useState('');
    const [role, setRole] = useState('');
    const [loading, setLoading] = useState(true);
    const [propertyConfig, setPropertyConfig] = useState({});
    const [images, setImages] = useState({});
    const [unsavedChanges, setUnsavedChanges] = useState(false);
    const [newImageKey, setNewImageKey] = useState('');

    const normalizePropertyOption = (rawProperty) => {
        if (!rawProperty || typeof rawProperty !== 'object') return null;

        const normalizedPropertyID = rawProperty.propertyID || rawProperty._id || rawProperty.id || '';
        const normalizedCompanyName = rawProperty.companyName || rawProperty.activeProperty || rawProperty.name || normalizedPropertyID;

        if (!normalizedPropertyID) return null;

        return {
            ...rawProperty,
            propertyID: normalizedPropertyID,
            companyName: normalizedCompanyName,
        };
    };

    useEffect(() => {
        const userDetails = getStoredUser();
        const selectedPropertyStr = localStorage.getItem('selectedProperty');

        let selectedProperty = null;

        try {
            if (selectedPropertyStr) {
                if (selectedPropertyStr.startsWith('{')) {
                    selectedProperty = JSON.parse(selectedPropertyStr);
                } else {
                    selectedProperty = selectedPropertyStr;
                }
            }
        } catch (e) {
            console.error('Error parsing selectedProperty:', e);
        }

        if (!userDetails) {
            navigate('/login');
            return;
        }

        setCompanyName(localStorage.getItem('selectedPropertyName') || 'UK Solar Admin');
        setRole(userDetails.role || '');

        if (propertyID) {
            setProperty(propertyID);
            if (selectedProperty && typeof selectedProperty === 'object' && selectedProperty.PList) {
                const normalizedList = selectedProperty.PList
                    .map(normalizePropertyOption)
                    .filter(Boolean);
                setPropList(normalizedList);
                const found = normalizedList.find((p) => p.propertyID === propertyID);
                if (found) {
                    setActivePropertyName(found.companyName);
                }
            }
            loadPropertyConfig(propertyID);
        } else if (selectedProperty) {
            const propID = typeof selectedProperty === 'object'
                ? (selectedProperty.propertyID || selectedProperty._id || selectedProperty.id)
                : selectedProperty;
            setProperty(propID);
            if (typeof selectedProperty === 'object') {
                const normalizedList = Array.isArray(selectedProperty.PList)
                    ? selectedProperty.PList.map(normalizePropertyOption).filter(Boolean)
                    : [];
                setPropList(normalizedList);
                setActivePropertyName(
                    selectedProperty.companyName || selectedProperty.activeProperty || selectedProperty.propertyID || propID
                );
            }
            loadPropertyConfig(propID);
        } else {
            navigate('/');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propertyID, navigate]);

    const loadPropertyConfig = async (propertyID) => {
        try {
            setLoading(true);
            const config = await getPropertyConfig(propertyID);
            setPropertyConfig(config);
            setImages(withManagedImageSlots(config.images));
        } catch (error) {
            console.error('Error loading property config:', error);
            Sentry.captureException(error);
            message.error(t('images.error.loading'));
        } finally {
            setLoading(false);
        }
    };

    const chooseProperty = (selectedPropID) => {
        const selectedProperty = propList.find((p) => p.propertyID === selectedPropID);
        if (selectedProperty) {
            localStorage.setItem('selectedProperty', selectedProperty.propertyID);
            localStorage.setItem('selectedPropertyName', selectedProperty.companyName);
            setActivePropertyName(selectedProperty.companyName);
            setProperty(selectedProperty.propertyID);
            loadPropertyConfig(selectedProperty.propertyID);
        }
    };

    const signOut = () => {
        localStorage.removeItem('userDetails');
        localStorage.removeItem('selectedProperty');
        localStorage.removeItem('selectedPropertyName');
        navigate('/login');
    };

    useEffect(() => {
        if (!isSuperAdmin(role)) return;

        getPropertyList()
            .then((data) => {
                const normalizedList = (Array.isArray(data) ? data : [])
                    .map(normalizePropertyOption)
                    .filter(Boolean);
                setPropList(normalizedList);

                if (property) {
                    const selected = normalizedList.find((p) => p.propertyID === property);
                    if (selected) {
                        setActivePropertyName(selected.companyName);
                    }
                }
            })
            .catch((error) => {
                console.error('Error loading property list:', error);
                Sentry.captureException(error);
            });
    }, [role, property]);

    const confirmIfDirty = (callback) => {
        if (unsavedChanges) {
            Modal.confirm({
                title: t('images.modals.unsavedTitle'),
                content: t('images.modals.unsavedDesc'),
                okText: t('images.modals.leave'),
                cancelText: t('common.cancel'),
                onOk: callback,
            });
        } else {
            callback();
        }
    };

    const handleImageUpload = (imageKey, data) => {
        setImages(prev => ({
            ...prev,
            [imageKey]: data.url,
        }));
        setUnsavedChanges(true);
        message.success(t('images.success.uploaded', { imageKey }));
    };

    const handleImageDelete = async (imageKey) => {
        const updatedImages = { ...images, [imageKey]: null };
        setImages(updatedImages);
        try {
            await updatePropertyImages(property, updatedImages);
            message.success(t('images.success.deleted', { imageKey }));
        } catch (error) {
            console.error('Error saving deletion:', error);
            Sentry.captureException(error);
            message.error(t('images.error.cloudinaryDelete'));
            setUnsavedChanges(true);
        }
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            await updatePropertyImages(property, images);
            setPropertyConfig(prev => ({
                ...prev,
                images,
            }));
            message.success(t('images.success.saved'));
            setUnsavedChanges(false);
        } catch (error) {
            console.error('Error saving images:', error);
            Sentry.captureException(error);
            message.error(t('images.error.saving'));
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        Modal.confirm({
            title: t('images.modals.discardTitle'),
            content: t('images.modals.discardDesc'),
            okText: t('images.modals.discard'),
            cancelText: t('common.cancel'),
            onOk: () => {
                setImages(withManagedImageSlots(propertyConfig.images));
                setUnsavedChanges(false);
                message.info(t('images.success.discarded'));
            },
        });
    };

    const handleAddNewImageField = () => {
        if (!newImageKey.trim()) {
            message.warning(t('images.error.fieldEmpty'));
            return;
        }

        const fieldName = newImageKey.trim().toLowerCase().replace(/\s+/g, '');

        if (images.hasOwnProperty(fieldName)) {
            message.warning(t('images.error.fieldExists'));
            return;
        }

        setImages(prev => ({
            ...prev,
            [fieldName]: null,
        }));
        setNewImageKey('');
        setUnsavedChanges(true);
        message.success(t('images.success.fieldAdded', { fieldName }));
    };

    const handleRemoveImageField = (imageKey) => {
        Modal.confirm({
            title: t('images.modals.removeTitle'),
            content: t('images.modals.removeDesc', { fieldName: imageKey }),
            okText: t('images.modals.remove'),
            okType: 'danger',
            cancelText: t('common.cancel'),
            onOk: () => {
                setImages(prev => {
                    const updated = { ...prev };
                    delete updated[imageKey];
                    return updated;
                });
                setUnsavedChanges(true);
                message.success(t('images.success.fieldRemoved', { fieldName: imageKey }));
            },
        });
    };

    const getImageDescriptions = IMAGE_SLOT_DESCRIPTIONS;

    const getDisplayName = (key) => {
        return key
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .trim();
    };

    return (
        <PageLayout
            companyName={companyName}
            chooseProperty={chooseProperty}
            propList={propList}
            activePropertyName={activePropertyName}
            property={property}
            role={role}
            signOut={signOut}
            confirmIfDirty={confirmIfDirty}
        >
            <Spin spinning={loading}>
                <div className='first-section'>
                    <div className='form-header'>
                        <Heading
                            text={t('images.title')}
                            fontSize={"3em"}
                            fontWeight={"bold"}
                            color={"black"}
                            textAlign={"center"}
                            margin={"0px 0px 0.5em 0px"}
                        />
                        <p style={{ textAlign: 'center', color: '#666', margin: '0px 0px 1em 0px' }}>
                            {t('images.subtitle', { propertyName: activePropertyName })}
                        </p>
                        <Divider />
                    </div>

                    <div style={{ marginBottom: '24px' }}>
                        {unsavedChanges && (
                            <Tag color="orange">{t('images.unsavedChanges')}</Tag>
                        )}
                    </div>

                    {Object.keys(images).length === 0 && !loading && (
                        <Empty
                            description={t('images.noImages')}
                            style={{ marginBottom: '32px' }}
                        />
                    )}

                    <Row gutter={[24, 24]}>
                        {sortImageSlots(Object.entries(images)).map(([key, value]) => (
                            <Col key={key} xs={24} sm={24} md={12} lg={12} xxl={8}>
                                <Card
                                    title={getDisplayName(key)}
                                    extra={
                                        <Space>
                                            {value ? <Tag color="success">{t('images.tags.uploaded')}</Tag> : <Tag>{t('images.tags.empty')}</Tag>}
                                            <Button
                                                type="text"
                                                danger
                                                size="small"
                                                onClick={() => handleRemoveImageField(key)}
                                                title={t('images.modals.remove')}
                                            >
                                                ×
                                            </Button>
                                        </Space>
                                    }
                                    style={{ height: '100%' }}
                                >
                                    <p style={{ color: '#666', fontSize: '12px', marginBottom: '16px' }}>
                                        {getImageDescriptions[key] || t('images.customField')}
                                    </p>
                                    <ImageUpload
                                        imageUrl={value}
                                        propertyID={property}
                                        onImageUpload={(data) => handleImageUpload(key, data)}
                                        onImageDelete={() => handleImageDelete(key)}
                                        maxSize={10}
                                    />
                                </Card>
                            </Col>
                        ))}
                    </Row>

                    <Divider style={{ marginTop: '32px', marginBottom: '32px' }} />

                    <Card title={t('images.addNewField')} style={{ marginBottom: '32px' }}>
                        <Space.Compact style={{ width: '100%' }}>
                            <Input
                                placeholder={t('images.fieldPlaceholder')}
                                value={newImageKey}
                                onChange={(e) => setNewImageKey(e.target.value)}
                                onPressEnter={handleAddNewImageField}
                                style={{ flex: 1 }}
                            />
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={handleAddNewImageField}
                            >
                                {t('images.addField')}
                            </Button>
                        </Space.Compact>
                        <p style={{ color: '#999', fontSize: '12px', marginTop: '8px' }}>
                            {t('images.fieldHelp')}
                        </p>
                    </Card>

                    <Divider />

                    <div style={{ textAlign: 'center', marginTop: '32px' }}>
                        <Space>
                            <Button
                                type="primary"
                                size="large"
                                onClick={handleSave}
                                disabled={!unsavedChanges || loading}
                            >
                                {t('images.save')}
                            </Button>
                            <Button
                                size="large"
                                onClick={handleReset}
                                disabled={!unsavedChanges || loading}
                            >
                                {t('images.discard')}
                            </Button>
                        </Space>
                    </div>

                    <div style={{ marginTop: '32px', padding: '16px', backgroundColor: '#f0f2f5', borderRadius: '4px' }}>
                        <h4>{t('images.tipsTitle')}</h4>
                        <ul style={{ marginBottom: 0 }}>
                            <li>{t('images.tips.storage')}</li>
                            <li>{t('images.tips.sizes')}</li>
                            <li>{t('images.tips.formats')}</li>
                            <li>{t('images.tips.maxSize')}</li>
                            <li>{t('images.tips.save')}</li>
                        </ul>
                    </div>
                </div>
            </Spin>
        </PageLayout>
    );
};

export default ImageManager;
